class SubxBundleSelector extends HTMLElement {
  constructor() {
    super();
    this.mode = 'subscribe';
  }

  connectedCallback() {
    this.form = this.querySelector('form');
    this.variantInput = this.querySelector('[data-variant-input]');
    this.sellingPlanInput = this.querySelector('[data-selling-plan-input]');
    this.submitPriceEl = this.querySelector('[data-submit-price]');
    this.giftRows = this.querySelectorAll('[data-gift-row]');
    this.savingsAmountEl = this.querySelector('[data-savings-amount]');
    this.discountNoteEl = this.querySelector('.subx-bundle__discount-note');
    this.tabs = this.querySelectorAll('.subx-bundle__tab');
    this.tiers = this.querySelectorAll('.subx-bundle__tier');

    const hasTabs = this.tabs.length > 0;
    this.mode = hasTabs ? 'subscribe' : 'one_time';

    this.tabs.forEach((tab) => {
      tab.addEventListener('click', () => this.setMode(tab.dataset.mode));
    });

    this.tiers.forEach((tier) => {
      const radio = tier.querySelector('input[type="radio"]');
      radio.addEventListener('change', () => this.selectTier(tier));
    });

    this.applyMode();

    const initialTier = this.querySelector('.subx-bundle__tier.is-selected') || this.tiers[0];
    if (initialTier) this.selectTier(initialTier);

    this.form.addEventListener('submit', (e) => this.handleSubmit(e));
  }

  setMode(mode) {
    this.mode = mode;
    this.applyMode();

    const activeTier = this.querySelector('.subx-bundle__tier.is-selected');
    if (activeTier) this.selectTier(activeTier);
  }

  applyMode() {
    this.tabs.forEach((tab) => {
      tab.setAttribute('aria-selected', tab.dataset.mode === this.mode ? 'true' : 'false');
    });

    this.querySelectorAll('[data-subtext-for]').forEach((el) => {
      el.hidden = el.dataset.subtextFor !== this.mode;
    });

    this.giftRows.forEach((row) => {
      row.hidden = this.mode !== 'subscribe';
    });

    this.querySelectorAll('[data-ribbon]').forEach((ribbon) => {
      ribbon.hidden = this.mode !== 'subscribe';
    });

    // Refresh every tier's price/compare-at/badge/per-stick text for the new mode.
    this.tiers.forEach((tier) => this.refreshTierDisplay(tier));
  }

  refreshTierDisplay(tier) {
    const isSub = this.mode === 'subscribe';

    const priceEl = tier.querySelector('.subx-bundle__price');
    const displayPrice = isSub ? priceEl.dataset.priceSub : priceEl.dataset.priceOtp;
    priceEl.textContent = this.formatMoney(displayPrice);

    const compareEl = tier.querySelector('.subx-bundle__compare-at');
    const compareText = isSub ? compareEl.dataset.compareSub : compareEl.dataset.compareOtp;
    compareEl.textContent = compareText || '';
    compareEl.hidden = !compareText;

    const badgeEl = tier.querySelector('.subx-bundle__badge');
    const badgeText = isSub ? badgeEl.dataset.badgeSub : badgeEl.dataset.badgeOtp;
    const badgePct = parseInt(badgeText.replace(/\D/g, ''), 10) || 0;
    badgeEl.textContent = badgeText;
    badgeEl.hidden = badgePct <= 0;

    const substextEl = tier.querySelector('.subx-bundle__tier-substext');
    if (substextEl) {
      substextEl.textContent = isSub
        ? substextEl.dataset.substextSub
        : substextEl.dataset.substextOtp;
    }
  }

  selectTier(tier) {
    this.tiers.forEach((t) => t.classList.remove('is-selected'));
    tier.classList.add('is-selected');
    tier.querySelector('input[type="radio"]').checked = true;

    const variantId = tier.dataset.variantId;
    const sellingPlanId = tier.dataset.sellingPlanId || '';
    this.variantInput.value = variantId;

    const isSubscribing = this.mode === 'subscribe' && sellingPlanId;

    if (isSubscribing) {
      this.sellingPlanInput.value = sellingPlanId;
      this.sellingPlanInput.disabled = false;
    } else {
      this.sellingPlanInput.value = '';
      this.sellingPlanInput.disabled = true;
    }

    const priceEl = tier.querySelector('.subx-bundle__price');
    const displayPrice = isSubscribing ? priceEl.dataset.priceSub : priceEl.dataset.priceOtp;
    if (this.submitPriceEl && displayPrice) {
      this.submitPriceEl.textContent = this.formatMoney(displayPrice);
    }

    // "Discount Auto-Applied" only makes sense when the selected tier, in the
    // current mode, actually carries a discount — read straight off the same
    // badge data already computed per-tier, nothing hardcoded.
    if (this.discountNoteEl) {
      const badgeEl = tier.querySelector('.subx-bundle__badge');
      const badgeText = this.mode === 'subscribe' ? badgeEl.dataset.badgeSub : badgeEl.dataset.badgeOtp;
      const badgePct = parseInt(badgeText.replace(/\D/g, ''), 10) || 0;
      this.discountNoteEl.hidden = badgePct <= 0;
    }

    // Dynamic "save an extra $X" amount for the one-time subtext: compare-at
    // (reference) price minus the subscription price, not the real OTP price
    // minus subscription price — matches how the strikethrough is framed.
    if (this.savingsAmountEl) {
      const diff = Number(priceEl.dataset.reference) - Number(priceEl.dataset.priceSub);
      this.savingsAmountEl.textContent = this.formatMoney(Math.max(diff, 0));
    }

    // Subscriber checklist only shows on the selected tier, and only in subscribe mode.
    this.tiers.forEach((t) => {
      const checklist = t.querySelector('[data-tier-checklist]');
      if (checklist) checklist.hidden = !(t === tier && this.mode === 'subscribe');
    });
  }

  formatMoney(cents) {
    // currencyDisplay: 'narrowSymbol' avoids Intl disambiguating USD/CAD/AUD
    // as "US$"/"CA$"/"A$" depending on the visitor's browser locale — without
    // it, a USD price can render as "US$37.49" instead of "$37.49" even
    // though USD is the default currency.
    return (Number(cents) / 100).toLocaleString(undefined, {
      style: 'currency',
      currency: (window.Shopify && Shopify.currency && Shopify.currency.active) || 'USD',
      currencyDisplay: 'narrowSymbol',
    });
  }

  async handleSubmit(event) {
    event.preventDefault();
    const submitBtn = this.querySelector('.subx-bundle__submit');
    submitBtn.disabled = true;

    const items = [
      {
        id: Number(this.variantInput.value),
        quantity: 1,
      },
    ];

    if (!this.sellingPlanInput.disabled && this.sellingPlanInput.value) {
      items[0].selling_plan = this.sellingPlanInput.value;
    }

    const activeTier = this.querySelector('.subx-bundle__tier.is-selected');
    const tierIndex = Array.from(this.tiers).indexOf(activeTier);
    const giftsApply = this.mode === 'subscribe' && tierIndex > 0;

    if (giftsApply) {
      const sprayVariantId = this.dataset.sprayVariantId;
      const giftVariantId = this.dataset.giftVariantId;
      if (sprayVariantId) items.push({ id: Number(sprayVariantId), quantity: 1 });
      if (giftVariantId) items.push({ id: Number(giftVariantId), quantity: 1 });
    }

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items }),
      });

      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.description || 'Could not add to cart');
      }

      // Adding via a plain fetch doesn't re-render the drawer's own markup —
      // the theme only does that from its own product-form submit handler.
      // Re-render it ourselves via Shopify's Section Rendering API before
      // opening it, or the drawer opens showing stale (e.g. empty) content.
      const drawerWrapper = document.getElementById('shopify-section-cart-drawer');
      if (drawerWrapper) {
        try {
          const sectionRes = await fetch('/?sections=cart-drawer');
          const sectionData = await sectionRes.json();
          if (sectionData['cart-drawer']) {
            drawerWrapper.outerHTML = sectionData['cart-drawer'];
          }
        } catch (sectionErr) {
          console.error('[subx-bundle-selector] cart-drawer refresh failed', sectionErr);
        }
      }

      // Open the drawer the same way a customer would — by clicking the
      // real cart icon — rather than guessing at the vendor theme's
      // internal open() API. Falls back to the cart page if that icon
      // isn't on the page for some reason.
      const cartIcon = document.getElementById('cart-icon-bubble');
      if (cartIcon) {
        cartIcon.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true }));
        submitBtn.disabled = false;
      } else {
        window.location.href = '/cart';
      }
    } catch (err) {
      console.error('[subx-bundle-selector]', err);
      submitBtn.disabled = false;
      alert('Something went wrong adding this to your cart. Please try again.');
    }
  }
}

customElements.define('subx-bundle-selector', SubxBundleSelector);
